import { createContext, useContext, useState, useEffect, useCallback } from "react";

/**
 * Contexto que permite a cualquier página/modal suprimir el modal bloqueante
 * de notificaciones críticas cuando el usuario está en medio de una tarea
 * crítica (crear pedido, crear cliente, importar contenedor, etc).
 *
 * Uso:
 *   const { pause, resume } = useNotifSuppress();
 *   useEffect(() => { pause(); return () => resume(); }, []);
 *
 * O el hook conveniente:
 *   useSuppressCritical(openBuilder);  // suprime mientras openBuilder === true
 */
const NotifSuppressContext = createContext({
  suppressed: false,
  pause: () => {},
  resume: () => {},
});

export function NotifSuppressProvider({ children }) {
  // Usamos un contador (no un booleano) para soportar múltiples supresores
  // simultáneos (por ej. un wizard con un modal anidado). Solo cuando el
  // contador llega a 0 se vuelve a mostrar.
  const [count, setCount] = useState(0);
  const pause = useCallback(() => setCount((c) => c + 1), []);
  const resume = useCallback(() => setCount((c) => Math.max(0, c - 1)), []);
  return (
    <NotifSuppressContext.Provider value={{ suppressed: count > 0, pause, resume }}>
      {children}
    </NotifSuppressContext.Provider>
  );
}

export function useNotifSuppress() {
  return useContext(NotifSuppressContext);
}

/**
 * Hook conveniente: mientras `active` sea true, el modal crítico queda pausado.
 * Auto-limpia al desmontar.
 */
export function useSuppressCritical(active) {
  const { pause, resume } = useNotifSuppress();
  useEffect(() => {
    if (!active) return undefined;
    pause();
    return () => resume();
  }, [active, pause, resume]);
}
